import { ColDef } from 'ag-grid-community';
import { BtnDefault } from '../common/Buttons';
import OpenAssetButton from '../common/OpenAssetButton';
import { IRequirement } from '../../types/brand';

interface ClientColumnParams {
  onView: (data: IRequirement) => void;
  onDelete?: (data: IRequirement) => void;
}

export const useRequirementColumns = ({
  onView,
}: ClientColumnParams): ColDef<IRequirement>[] => {
  return [
    ...requirementColumnsDefs(),
    {
      headerName: 'Actions',
      field: '_id',
      sortable: false,
      filter: false,
      maxWidth: 85,
      suppressSizeToFit: true,
      pinned: 'right',
      lockPinned: true,
      cellRenderer: (params: any) => (
        <div className="flex gap-2 p-1">
          <BtnDefault
            onClick={() => {
              onView(params.data);
            }}
          >
            View
          </BtnDefault>
        </div>
      ),
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
      },
    },
  ];
};

const getStatusColor = (status: string) => {
  if (!status) return '';
  if (status === 'fulfilled') return '#60a5fa'; // bg-blue-200
  if (status === 'sourced') return '#4ade80'; // bg-green-400
  if (status === 'active') return '#facc15'; // bg-yellow-400
  if (status === 'postponed') return '#818cf8'; // bg-indigo-400
  if (status === 'dropped') return '#f87171'; // bg-red-400
  if (status === 'reopened') return '#a855f7'; // bg-purple-400
};

const getFirstName = (agent: any) => {
  if (!agent) return '';
  const name = agent.name || agent.personalInfo?.firstName;
  return name?.split(' ')[0];
};

const renderAssetLink = (label: string) => (params: any) => {
  const key = params.value;

  return <OpenAssetButton assetKey={key} name={label} className="underline" />;
};

export const requirementColumnsDefs = (): ColDef<IRequirement>[] => [
  {
    headerName: 'Client Name',
    field: 'clientName',
    minWidth: 150,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Type',
    field: 'type',
    minWidth: 180,
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Expected Hiring By',
    field: 'expectedHiringBy',
    valueGetter: (params) => {
      if (!params.data?.expectedHiringBy) return '';
      return new Date(params.data.expectedHiringBy);
    },
    minWidth: 160,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Date Raised',
    field: 'createdAt',
    minWidth: 160,
    valueGetter: (params) => {
      if (!params.data?.createdAt) return '';
      return new Date(params.data.createdAt);
    },
    suppressSizeToFit: true,
    sortable: true,
    filter: true,
  },
  {
    headerName: 'Raised By',
    field: 'raisedBy',
    minWidth: 120,
    filter: true,
    suppressSizeToFit: true,
    sortable: true,
  },
  {
    headerName: 'Status',
    field: 'status',
    valueGetter: (params) => {
      if (!params.data?.status) return '';
      return (
        params.data.status.charAt(0).toUpperCase() + params.data.status.slice(1)
      );
    },
    cellStyle: (params: any) => {
      const statusColor = getStatusColor(
        (params.data?.status || '').toLowerCase()
      );
      return statusColor ? { color: statusColor } : undefined;
    },
    minWidth: 120,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Matched Agents',
    field: 'agents',
    valueGetter: (params) => {
      if (!params.data?.agents) return '';
      return params.data.agents
        .map((agent: any) => getFirstName(agent))
        .join(', ');
    },
    minWidth: 200,
    filter: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Remarks',
    field: 'comments',
    minWidth: 400,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Website',
    field: 'website',
    minWidth: 120,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Timezone',
    field: 'timezone',
    minWidth: 120,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Point of Contact',
    field: 'clientPOC',
    minWidth: 120,
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Country',
    field: 'country',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Industry',
    field: 'industry',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Relevant Industries',
    field: 'relevantIndustries',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Languages',
    field: 'languages',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Channels',
    field: 'channels',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Attachments',
    field: 'attachments',
    valueGetter: (params) => params.data?.attachments?.[0]?.fileKey || '',
    cellRenderer: renderAssetLink('Attachment'),
    minWidth: 180,
    filter: true,
    sortable: false,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Engagement',
    field: 'engagement',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Tickets',
    field: 'tickets',
    filter: true,
    sortable: true,
    suppressSizeToFit: true,
  },
];
